/**
 * @file BinCard.tsx
 * @description Карточка ячейки в справочнике (phase19.14, W14; сводка
 * содержимого и «Положить товар» — phase19.23, A4).
 *
 * Карточка отвечает на два вопроса без открытия панели: пусто ли в ячейке и
 * что там лежит (`summary` из `GET /admin/wms/bins`). Редкие действия — в
 * меню «⋯», деструктивное последним (чек-лист A6, п. 6). Подписи и меню —
 * общие с таблицей `≥lg` (`bin-view.ts`).
 *
 * Галочка «под печать этикеток» (phase19.22 шаг 5) — только у активной
 * ячейки и только при `onToggleSelect`; зона нажатия 48px.
 */

import { PackagePlus } from "lucide-react";

import { binRowActions, binStatus, binSummaryText } from "./bin-view";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { formatBinHuman, type StorageBinDto } from "../../../lib/api-bins";
import { cn } from "@/lib/utils";

interface Props {
  bin: StorageBinDto;
  onOpen: () => void;
  /** Диалог «Положить товар» — быстрое размещение партии в эту ячейку. */
  onPut: () => void;
  onArchive: () => void;
  onRestore: () => void;
  /** Отметка под печать этикеток; не передан — галочки нет. */
  selected?: boolean;
  onToggleSelect?: () => void;
}

export function BinCard({ bin, onOpen, onPut, onArchive, onRestore, selected = false, onToggleSelect }: Props) {
  const status = binStatus(bin);

  return (
    <li
      className={cn(
        "flex flex-col gap-1 rounded-xl border bg-surface-lowest p-3",
        selected ? "border-primary" : "border-on-surface/8",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* Зона нажатия галочки и адреса — высота кнопки темы (48, у
            кладовщика 56): квадрат 20px и ссылка 24px не попадались пальцем
            (дефект приёмки 2026-10-02, 360). */}
        {onToggleSelect && bin.isActive ? (
          <label className="-my-1 -ml-2 inline-flex size-[var(--control-h,3rem)] cursor-pointer items-center justify-center">
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              aria-label={`Выбрать ячейку ${bin.code} для печати этикеток`}
              className="size-5"
            />
          </label>
        ) : null}
        <button
          type="button"
          onClick={onOpen}
          className="-my-1 inline-flex min-h-[var(--control-h,3rem)] items-center rounded-md px-1 font-mono text-base font-medium text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {bin.code}
        </button>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>
      <span className="text-xs text-on-surface-variant">{formatBinHuman(bin)}</span>
      <span className="text-sm tabular-nums">{binSummaryText(bin)}</span>
      {bin.summary && bin.summary.skuCodes.length > 0 ? (
        <span className="truncate font-mono text-xs text-on-surface-variant">
          {bin.summary.skuCodes.join(" · ")}
          {bin.summary.skuCount > bin.summary.skuCodes.length
            ? ` и ещё ${bin.summary.skuCount - bin.summary.skuCodes.length}`
            : ""}
        </span>
      ) : null}

      {/* flex-wrap: на 360px кнопки переносятся, а не уезжают за край. */}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {bin.isActive ? (
          <Button variant="outline" onClick={onPut}>
            <PackagePlus className="size-4" />
            <span className="ml-1.5">Положить товар</span>
          </Button>
        ) : null}
        <RowActionsMenu
          // Размер — из темы (SC44: 56px только у кладовщика до 1024).
          size="compact"
          actions={binRowActions(bin, { onOpen, onArchive, onRestore })}
          title={`Действия с ячейкой ${bin.code}`}
          ariaLabel={`Другие действия с ячейкой ${bin.code}`}
        />
      </div>
    </li>
  );
}
